export const isEmptyQuoteValue = (value) =>
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')

const isGeneralFieldVisible = (field, quoteType, isEmptyContainer) => {
    if (field.IsHiddenList === true) return false
    if (field.Name === 'AddressName' && quoteType !== 'Specific Price') return false
    if (field.Name === 'CountryCode' && !['Postal Code', 'Location Set'].includes(quoteType)) return false
    if (field.Name === 'TimeChargeOrDischarge' && quoteType === 'Transfer') return false
    if (field.Name === 'MinimunVoyages' && !isEmptyContainer) return false

    if (
        ['IsEmptyContainer', 'NavCompany', 'Container', 'MinimunVoyages'].includes(field.Name) &&
        quoteType !== 'Transfer'
    ) {
        return false
    }

    return true
}

const isPriceFieldVisible = (fieldName, quoteType) => {
    if (fieldName === 'SimulatedUnitPrice') return false

    if (quoteType === 'Range Kilometers') {
        return fieldName !== 'SimulatedVariableValue'
    }

    if (['Postal Code', 'Location Set', 'Specific Price'].includes(quoteType)) {
        return !['StandardExtraLimitPrice', 'ReloadExtraLimitPrice', 'ReloadPrice', 'Limit'].includes(fieldName)
    }

    if (quoteType === 'Transfer') {
        return ![
            'SimulatedVariableValue',
            'ReloadExtraLimitPrice',
            'ReloadPrice',
            'StandardPrice',
            'Limit'
        ].includes(fieldName)
    }

    return true
}

export const normalizeAdditionalCosts = (costs = []) =>
    costs.map((cost) => ({
        ...cost,
        AddicionalPrice: cost?.AddicionalPrice ?? cost?.AdditionalPrice ?? '',
        Min: cost?.Min ?? cost?.Minimum ?? cost?.Minimun ?? ''
    }))

export const validateQuote = ({ dataQuotes, optionsForm, optionsRange, tablesRange, rowCost }) => {
    const quoteType = dataQuotes.get('TypeQuotes')
    const isEmptyContainer = dataQuotes.get('IsEmptyContainer') === true

    const missingRequiredField = optionsForm
        .filter((field) => field.IsRequired && field.Name !== 'PricePlanId')
        .filter((field) => isGeneralFieldVisible(field, quoteType, isEmptyContainer))
        .find((field) => {
            const fieldName = field.IsForeignKey && field.ForeignTableToPassId
                ? field.ForeignTableToPassId
                : field.Name

            return isEmptyQuoteValue(dataQuotes.get(fieldName))
        })

    if (missingRequiredField) {
        return {
            key: 'RequiredField',
            title: missingRequiredField.NormalizedName
        }
    }

    if (!quoteType) {
        return { key: 'QuoteTypeRequired' }
    }

    if (quoteType === 'Specific Price') {
        const addressField = optionsForm.find((field) => field.Name === 'AddressName')
        const addressFieldName = addressField?.ForeignTableToPassId || 'AddressName'

        if (isEmptyQuoteValue(dataQuotes.get(addressFieldName))) {
            return {
                key: 'RequiredField',
                title: addressField?.NormalizedName || 'Address'
            }
        }
    }

    if (quoteType === 'Location Set') {
        const location = dataQuotes.get('Location')
        if (
            isEmptyQuoteValue(location) ||
            (Array.isArray(location) && location.length === 0)
        ) {
            return { key: 'DrawPolygonNecessary' }
        }
    }

    if (!Array.isArray(tablesRange) || tablesRange.length === 0) {
        return { key: 'QuoteRequiresPriceTable' }
    }

    if (!Array.isArray(optionsRange) || optionsRange.length === 0) {
        return { key: 'QuotePriceConfigurationLoading' }
    }

    const visiblePriceOptions = (optionsRange || [])
        .filter((field) => field.IsHiddenList !== true)
        .filter((field) => isPriceFieldVisible(field.Name, quoteType))
    const requiredPriceFields = visiblePriceOptions
        .filter((field) => field.IsRequired === true)
        .map((field) => field.Name)
    const meaningfulPriceFields = visiblePriceOptions
        .map((field) => field.Name)
        .filter((fieldName) => fieldName !== 'Limit')
    const tableNames = tablesRange.map((table) => String(table?.Name || '').trim())

    if (tableNames.some((tableName) => tableName === '')) {
        return { key: 'EmptyPriceTableName' }
    }

    if (new Set(tableNames).size !== tableNames.length) {
        return { key: 'DuplicatePriceTableName' }
    }

    for (const table of tablesRange) {
        if (!Array.isArray(table.PriceTableDetailList) || table.PriceTableDetailList.length === 0) {
            return { key: 'QuoteRequiresPriceTier' }
        }

        const hasIncompleteTier = table.PriceTableDetailList.some((detail) => {
            if (requiredPriceFields.some((fieldName) => isEmptyQuoteValue(detail?.[fieldName]))) {
                return true
            }

            const fieldsToCheck = meaningfulPriceFields.length > 0
                ? meaningfulPriceFields
                : visiblePriceOptions.map((field) => field.Name)

            if (fieldsToCheck.length === 0) {
                return Object.entries(detail || {})
                    .filter(([fieldName]) => !['Id', 'id', 'PriceTableId', 'PricePlanId'].includes(fieldName))
                    .every(([, value]) => isEmptyQuoteValue(value))
            }

            return fieldsToCheck.every((fieldName) => isEmptyQuoteValue(detail?.[fieldName]))
        })

        if (hasIncompleteTier) {
            return { key: 'EmptyPriceTierLine' }
        }
    }

    const routeLines = quoteType === 'Transfer'
        ? dataQuotes.get('PricePlanDetailAddressList')
        : dataQuotes.get('PricePlanDetailList')

    if (!Array.isArray(routeLines) || routeLines.length === 0) {
        return { key: quoteType === 'Transfer' ? 'QuoteRequiresAddressLine' : 'QuoteRequiresZoneLine' }
    }

    const hasIncompleteRoute = routeLines.some((line) => {
        const fromValue = quoteType === 'Transfer' ? line?.FromAddressId : line?.FromPriceZoneId
        const toValue = quoteType === 'Transfer' ? line?.ToAddressId : line?.ToPriceZoneId

        return isEmptyQuoteValue(fromValue) ||
            isEmptyQuoteValue(toValue) ||
            isEmptyQuoteValue(line?.PriceTable) ||
            !tableNames.includes(String(line.PriceTable).trim())
    })

    if (hasIncompleteRoute) {
        const hasInvalidTableReference = routeLines.some((line) =>
            !isEmptyQuoteValue(line?.PriceTable) &&
            !tableNames.includes(String(line.PriceTable).trim())
        )

        if (hasInvalidTableReference) {
            return { key: 'InvalidPriceTableReference' }
        }

        return { key: quoteType === 'Transfer' ? 'EmptyAddressLine' : 'EmptyZoneLine' }
    }

    const hasEmptyAdditionalCost = rowCost.some((cost) =>
        isEmptyQuoteValue(cost?.Descriptive) ||
        isEmptyQuoteValue(cost?.AddicionalPrice ?? cost?.AdditionalPrice) ||
        isEmptyQuoteValue(cost?.Min ?? cost?.Minimum ?? cost?.Minimun)
    )

    if (hasEmptyAdditionalCost) {
        return { key: 'EmptyAdditionalCostLine' }
    }

    return null
}
